import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Zap } from 'lucide-react';
import { EncryptedImage } from '../EncryptedImage';
import { useLanguage } from '../../contexts/LanguageContext';
import { freeComics } from '../../data/comics';
import { useHorizontalDragScroll } from '../../hooks/useHorizontalDragScroll';

export const FreeComicsSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const scrollRef = useHorizontalDragScroll();

  return (
    <div style={styles.freeComicsSection}>
      <div style={styles.freeComicsHeader}>
        <h3 className="fp-ribbon fp-ribbon--cyan" style={styles.freeComicsTitle}>
          <Zap size={17} strokeWidth={3} fill="#ffd400" style={styles.titleIcon} />
          {t('freeComicsSection')}
        </h3>
        <p style={styles.freeComicsDescription}>{t('freeComicsDescription')}</p>
      </div>
      <div className="fp-noscrollbar" style={styles.freeComicsGrid} ref={scrollRef}>
        {freeComics.map((comic, index) => (
          <div 
            key={comic.id} 
            className="fp-card"
            style={styles.freeComicCard}
            onClick={() => navigate(`/free-comic/${comic.id}`)}
            data-testid={`free-comic-${comic.id}`}
          >
            <div className="fp-cover" style={styles.coverContainer}>
              <EncryptedImage
                src={comic.coverUrl}
                alt={comic.title}
                style={styles.comicCover}
                priority={index < 3}
              />
              <span className="fp-chip fp-chip--lime" style={styles.freeBadge}>{t('free')}</span>
            </div>
            <h3 style={styles.comicTitle}>{comic.title}</h3>
            <button
              className="fp-btn fp-btn--sm fp-btn--cyan fp-btn--block"
              data-testid={`read-free-btn-${comic.id}`}
            >
              <BookOpen size={15} strokeWidth={3} />
              {t('readFree')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  freeComicsSection: {
    position: 'relative',
    padding: '26px 0 44px',
  },
  freeComicsHeader: {
    textAlign: 'center',
    marginBottom: '18px',
    padding: '0 14px',
  },
  freeComicsTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    maxWidth: '100%',
  },
  titleIcon: {
    display: 'inline-block',
    verticalAlign: '-2px',
    marginRight: '6px',
  },
  freeComicsDescription: {
    fontSize: '13px',
    color: '#fff',
    margin: 0,
    opacity: 0.9,
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
  },
  freeComicsGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: '14px',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '4px 14px 10px',
    WebkitOverflowScrolling: 'touch',
    cursor: 'grab',
    scrollSnapType: 'x proximity',
    scrollPaddingLeft: '14px',
  },
  freeComicCard: {
    width: '44vw',
    maxWidth: '190px',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'relative',
    cursor: 'pointer',
    scrollSnapAlign: 'start',
  },
  coverContainer: {
    position: 'relative',
    width: '100%',
  },
  comicCover: {
    width: '100%',
    aspectRatio: '2/3',
    objectFit: 'cover',
    display: 'block',
  },
  comicTitle: {
    margin: '12px 2px 9px',
    fontSize: '13px',
    fontWeight: 800,
    color: '#fff',
    textAlign: 'center',
    height: '33px',
    lineHeight: '1.25',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    textShadow: '0 2px 0 rgba(0,0,0,0.5)',
  },
  freeBadge: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 2,
  },
};

export default FreeComicsSection;
